import { forwardRef, type CSSProperties } from 'react';
import { cn } from '../../lib/cn';

export type SCircleProgressType = 'primary' | 'inverse' | 'error' | 'complete';

const VIEWBOX = 80;
const RADIUS = 34;
const STROKE = 12;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const COLOR_MAP: Record<SCircleProgressType, { arc: string; track: string }> = {
  primary: { arc: 'var(--cmp-progress-active-color)', track: 'var(--cmp-progress-color-track)' },
  // inverse: 어두운 배경용 — arc 흰색, track 은 흰색 40%(회색 링)
  inverse: { arc: 'var(--color-white)', track: 'rgba(255,255,255,0.4)' },
  error: { arc: 'var(--color-red-75)', track: 'var(--cmp-progress-color-track)' },
  complete: { arc: '#01BB4B', track: 'var(--cmp-progress-color-track)' },
};

export interface SCircleProgressProps {
  /** 진행률 (0–100) */
  value?: number;
  /** 색상 테마 */
  type?: SCircleProgressType;
  /** 불확정(스피너) 모드 — value 무시 */
  indeterminate?: boolean;
  /** 하단 레이블 */
  label?: string;
  /** true면 퍼센트를 원 아래가 아닌 원 가운데에 표시 */
  innerValue?: boolean;
  className?: string;
  style?: CSSProperties;
}

const clamp = (v: number) => (Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 0);

/** SCircleProgress — sd-circle-progress 포팅. determinate=진행률 arc, indeterminate=스피너. */
export const SCircleProgress = forwardRef<HTMLDivElement, SCircleProgressProps>(
  function SCircleProgress(
    {
      value = 0,
      type = 'primary',
      indeterminate = false,
      label,
      innerValue = false,
      className,
      style,
    },
    ref,
  ) {
    const v = clamp(value);
    const colors = COLOR_MAP[type];
    const c = VIEWBOX / 2;
    const percentColor = v === 0 ? 'var(--sys-color-fg-tertiary, #888888)' : colors.arc;
    const filled = (v / 100) * CIRCUMFERENCE;
    const showPercent = !indeterminate;
    const percentNode = (
      <span
        className={cn(
          'text-center text-[16px] font-bold',
          innerValue
            ? // leading-[normal]: 26px leading 은 폰트 content(19px) 위아래로 홀수(7px)만큼 남아
              // 3px/4px 로 쪼개지고 글자가 링 중심보다 위로 뜬다. (leading-normal 은 1.5 라 동일 문제)
              'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 leading-[normal]'
            : 'leading-[26px]',
        )}
        style={{ color: percentColor }}
      >
        {Math.round(v)}%
      </span>
    );

    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex flex-col items-center gap-[var(--cmp-progress-circular-gap)]',
          className,
        )}
        style={style}
      >
        <div className={cn(innerValue && 'relative')}>
          <svg
            viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
            xmlns="http://www.w3.org/2000/svg"
            className={cn(
              'h-[80px] w-[80px]',
              indeterminate && 'origin-center animate-circle-spin',
            )}
          >
            <circle
              cx={c}
              cy={c}
              r={RADIUS}
              fill="none"
              strokeWidth={STROKE}
              style={{ stroke: colors.track }}
            />
            <circle
              cx={c}
              cy={c}
              r={RADIUS}
              fill="none"
              strokeWidth={STROKE}
              strokeLinecap="round"
              className={cn(
                'origin-center',
                indeterminate
                  ? 'animate-circle-arc'
                  : '-rotate-90 transition-[stroke-dasharray] duration-[400ms] ease-linear',
              )}
              style={{
                stroke: colors.arc,
                ...(indeterminate
                  ? {}
                  : { strokeDasharray: `${filled} ${CIRCUMFERENCE}`, strokeDashoffset: 0 }),
              }}
            />
          </svg>
          {/* innerValue: 원 가운데에 퍼센트 오버레이 */}
          {showPercent && innerValue && percentNode}
        </div>
        {/* 기본: 퍼센트를 원(ring) 아래에 표시 */}
        {showPercent && !innerValue && percentNode}
        {label && (
          <span className="text-center text-[12px] font-normal leading-[20px] text-[color:var(--cmp-progress-color-label)]">
            {label}
          </span>
        )}
      </div>
    );
  },
);
